/**
 * Arc text for production files — a VECTOR port of the builder's
 * `useArcText.ts` (Clone of builder v2, `src/composables/scene/useArcText.ts`).
 *
 * The builder renders a text placement with `arc_enabled` in one of three ways
 * and persists only the parameters (mode, strength, flip, variant), so the
 * production file has to re-derive the curve from the same maths or the
 * customer's arched name prints straight:
 *
 *   - simple           : glyphs laid along a circular arc, tangent-rotated,
 *                        at the straight render size (72).
 *   - advanced / loft  : the same circular layout at the advanced render
 *                        size (240) — the size compensation 72/240 brings the
 *                        letters back to the straight height.
 *   - advanced / bottom, butterfly : the builder warps a RASTER of the straight
 *                        text column by column along a sine arch. Here the
 *                        straight glyph outlines are flattened and every point
 *                        is pushed through the same column mapping, so the
 *                        print stays vector (laser/plotter safe) while matching
 *                        what the customer saw.
 *
 * Everything is expressed in the mode's render font size; the caller applies
 * the builder's canonical-scale compensation (`arcSizeCompensation`).
 */

export const STRAIGHT_TEXT_RENDER_FONT_SIZE = 72;
export const ARC_RENDER_FONT_SIZES = { simple: 72, advanced: 240 } as const;
export type ArcMode = keyof typeof ARC_RENDER_FONT_SIZES;
export type ArcVariant = 'loft' | 'bottom' | 'butterfly';

/** Text item fields the builder persists for an arc placement. */
export interface ArcTextFields {
  arc_enabled?: boolean | null;
  arc_mode?: string | null;
  arc_strength?: number | string | null;
  arc_flipped?: boolean | null;
  arc_variant?: string | null;
  /** True once the persisted scale is canonical (straight-bbox space). */
  arc_scale_canonical?: boolean | null;
  /** Product-level permission; the builder never arcs a placement without it. */
  arc_text_allowed?: boolean | null;
}

export interface ArcTextParams {
  mode: ArcMode;
  /** 0-100; simple: curvature; advanced: curve + text-height compression. */
  strength?: number;
  /** Circular layouts only: invert the sweep direction. */
  flipped?: boolean;
  variant?: ArcVariant;
  /** Outline width in straight-text (72) units; 0 for none. */
  strokeWidth?: number;
}

/** The slice of opentype.js we use, typed loosely (builds differ). */
export interface ArcPathLike {
  commands?: PathCommand[];
  toPathData?: (decimalPlaces?: number) => string;
  toSVG?: (decimalPlaces?: number) => string;
}
export interface ArcGlyphLike {
  advanceWidth?: number;
  getPath: (x: number, y: number, fontSize: number) => ArcPathLike;
}
export interface ArcFontLike {
  unitsPerEm?: number;
  charToGlyph?: (char: string) => ArcGlyphLike | undefined;
  getAdvanceWidth?: (text: string, fontSize: number) => number;
  getPath: (
    text: string,
    x: number,
    y: number,
    fontSize: number,
    options?: { features?: { liga?: boolean; rlig?: boolean } }
  ) => ArcPathLike;
}

export type PathCommand =
  | { type: 'M' | 'L'; x: number; y: number }
  | { type: 'Q'; x1: number; y1: number; x: number; y: number }
  | { type: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { type: 'Z' };

export interface ArcTextLayout {
  /** `<path …/>` markup: one per glyph (circular) or one for the whole text (warped). */
  paths: string[];
  renderFontSize: number;
  /** Stroke width to paint at the render size (the builder's parity rules). */
  renderStrokeWidth: number;
  /**
   * Where the builder's object centre sits relative to the glyph geometry's
   * centre, in render units. Zero for circular layouts (the builder's object
   * IS the glyph group); non-zero for warped ones, whose builder object is the
   * warped raster canvas — padding and arch headroom included.
   */
  centerOffset: { x: number; y: number };
}

export function effectiveArcMode(raw: string | null | undefined): ArcMode {
  return raw === 'simple' ? 'simple' : 'advanced';
}

/** Canonical (straight, 72) scale → applied scale on the arc-rendered object. */
export function arcSizeCompensation(mode: ArcMode | null | undefined): number {
  if (mode === null || mode === undefined) return 1;
  const render = ARC_RENDER_FONT_SIZES[mode];
  return render > 0 ? STRAIGHT_TEXT_RENDER_FONT_SIZE / render : 1;
}

/** Whether a text item is rendered arched in the builder. */
export function wantsArcText(item: ArcTextFields): boolean {
  return !!item.arc_enabled && item.arc_text_allowed !== false;
}

export function layoutArcText(
  font: ArcFontLike,
  value: string,
  params: ArcTextParams
): ArcTextLayout | null {
  if (!value?.trim()) return null;
  const strength = clampStrength(params.strength);
  const strokeWidth = Math.max(0, params.strokeWidth ?? 0);
  if (params.mode === 'simple') {
    return circularLayout(font, value, strength, !!params.flipped, ARC_RENDER_FONT_SIZES.simple, strokeWidth);
  }
  // AB#17319: the builder routes the advanced "loft" variant through the
  // circular geometry at the advanced render size; bottom/butterfly warp.
  if ((params.variant ?? 'loft') === 'loft') {
    return circularLayout(font, value, strength, !!params.flipped, ARC_RENDER_FONT_SIZES.advanced, strokeWidth);
  }
  return warpedLayout(font, value, strength, params.variant as ArcVariant, strokeWidth);
}

function clampStrength(raw: number | undefined): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 25;
  return Math.min(100, Math.max(0, n));
}

/**
 * Port of the builder's `createSimpleArc`: strength 0..100 → sweep 0..~150°;
 * each glyph is drawn about its own advance centre, then translated onto the
 * arc and rotated to the tangent. `flipped` inverts the sweep.
 */
function circularLayout(
  font: ArcFontLike,
  value: string,
  strength: number,
  flipped: boolean,
  fontSize: number,
  strokeWidth: number
): ArcTextLayout | null {
  const unitsPerEm = font.unitsPerEm ?? 1000;
  const glyphs: { glyph: ArcGlyphLike; advance: number }[] = [];
  let totalWidth = 0;
  for (const ch of Array.from(value)) {
    const glyph = font.charToGlyph?.(ch);
    if (!glyph) continue;
    const advance = ((glyph.advanceWidth ?? 0) * fontSize) / unitsPerEm;
    glyphs.push({ glyph, advance });
    totalWidth += advance;
  }
  if (totalWidth <= 0 || !glyphs.length) return null;

  const maxSweepRad = (150 * Math.PI) / 180;
  const sweep = Math.max(0.001, (strength / 100) * maxSweepRad);
  const radius = totalWidth / sweep;
  const direction = flipped ? -1 : 1;

  const paths: string[] = [];
  let xCursor = 0;
  for (const { glyph, advance } of glyphs) {
    const glyphCenterX = xCursor + advance / 2;
    const theta = ((glyphCenterX - totalWidth / 2) / totalWidth) * sweep;
    const cx = radius * Math.sin(theta);
    const cy = direction * (radius - radius * Math.cos(theta));
    const d = pathData(glyph.getPath(-advance / 2, 0, fontSize));
    xCursor += advance;
    if (!d) continue;
    const rotDeg = (theta * 180 * direction) / Math.PI;
    paths.push(
      `<path d="${d}" transform="translate(${cx.toFixed(2)} ${cy.toFixed(2)}) rotate(${rotDeg.toFixed(2)})"/>`
    );
  }
  if (!paths.length) return null;
  return { paths, renderFontSize: fontSize, renderStrokeWidth: strokeWidth, centerOffset: { x: 0, y: 0 } };
}

/** The column mapping of the builder's `createAdvancedArc` raster warp. */
export interface WarpSpec {
  minX: number;
  minY: number;
  padding: number;
  srcWidth: number;
  srcHeight: number;
  amp: number;
  topPad: number;
  topRatio: number;
  dstWidth: number;
  dstHeight: number;
}

export function buildWarpSpec(
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
  measuredWidth: number | null,
  strength: number,
  variant: ArcVariant,
  renderStrokeWidth: number
): WarpSpec {
  // Padding leaves room for half the rendered stroke; 8 with no outline.
  const padding = Math.ceil(renderStrokeWidth / 2) + 8;
  const srcWidth = Math.ceil(Math.max(measuredWidth ?? 0, bbox.maxX - bbox.minX) + padding * 2);
  const srcHeight = Math.ceil(bbox.maxY - bbox.minY + padding * 2);
  // Amplitude from the glyph height plus the base margin only — the outline
  // width never reshapes the arc.
  const curveHeight = Math.ceil(bbox.maxY - bbox.minY + 8 * 2);
  const amp = (strength / 100) * (curveHeight * 0.9);
  const offsetY = variant === 'bottom' ? 50 : variant === 'butterfly' ? 25 : 0;
  const topPad = Math.ceil(amp) + 2;
  return {
    minX: bbox.minX,
    minY: bbox.minY,
    padding,
    srcWidth,
    srcHeight,
    amp,
    topPad,
    topRatio: Math.max(0, (50 - offsetY) / 50),
    dstWidth: srcWidth,
    dstHeight: srcHeight + topPad + 2,
  };
}

/** Map a point of the straight glyph outline into the warped canvas. */
export function warpPoint(spec: WarpSpec, px: number, py: number): { x: number; y: number } {
  const cx = px - spec.minX + spec.padding;
  const cy = py - spec.minY + spec.padding;
  const t = cx / Math.max(1, spec.srcWidth - 1);
  const curve = Math.sin(t * Math.PI) * spec.amp;
  const shiftTop = curve * spec.topRatio;
  const destY = spec.topPad - shiftTop;
  const destHeight = spec.srcHeight + shiftTop - curve;
  return { x: cx, y: destY + (cy * destHeight) / spec.srcHeight };
}

const CURVE_SEGMENTS = 8;

function warpedLayout(
  font: ArcFontLike,
  value: string,
  strength: number,
  variant: ArcVariant,
  strokeWidth: number
): ArcTextLayout | null {
  const fontSize = ARC_RENDER_FONT_SIZES.advanced;
  const path = font.getPath(value, 0, 0, fontSize, { features: { liga: true, rlig: true } });
  const commands = path.commands ?? [];
  const bbox = commandsBBox(commands);
  if (!bbox) return null;
  const measured =
    typeof font.getAdvanceWidth === 'function' ? font.getAdvanceWidth(value, fontSize) : null;
  // The raster is drawn at 240 and shrunk by 72/240 afterwards, so the stroke
  // is pre-scaled to keep the visible outline as thick as on straight text.
  const renderStrokeWidth =
    strokeWidth > 0 ? strokeWidth * (fontSize / STRAIGHT_TEXT_RENDER_FONT_SIZE) : 0;
  const spec = buildWarpSpec(bbox, measured, strength, variant, renderStrokeWidth);

  const out: string[] = [];
  let cur = { x: 0, y: 0 };
  let start = cur;
  let gMinX = Infinity;
  let gMinY = Infinity;
  let gMaxX = -Infinity;
  let gMaxY = -Infinity;
  const emit = (cmd: 'M' | 'L', x: number, y: number) => {
    const p = warpPoint(spec, x, y);
    gMinX = Math.min(gMinX, p.x);
    gMinY = Math.min(gMinY, p.y);
    gMaxX = Math.max(gMaxX, p.x);
    gMaxY = Math.max(gMaxY, p.y);
    out.push(`${cmd}${p.x.toFixed(2)} ${p.y.toFixed(2)}`);
  };
  for (const c of commands) {
    switch (c.type) {
      case 'M':
        cur = { x: c.x, y: c.y };
        start = cur;
        emit('M', c.x, c.y);
        break;
      case 'L':
        cur = { x: c.x, y: c.y };
        emit('L', c.x, c.y);
        break;
      case 'Q':
        for (let i = 1; i <= CURVE_SEGMENTS; i++) {
          const t = i / CURVE_SEGMENTS;
          const mt = 1 - t;
          emit(
            'L',
            mt * mt * cur.x + 2 * mt * t * c.x1 + t * t * c.x,
            mt * mt * cur.y + 2 * mt * t * c.y1 + t * t * c.y
          );
        }
        cur = { x: c.x, y: c.y };
        break;
      case 'C':
        for (let i = 1; i <= CURVE_SEGMENTS; i++) {
          const t = i / CURVE_SEGMENTS;
          const mt = 1 - t;
          emit(
            'L',
            mt * mt * mt * cur.x + 3 * mt * mt * t * c.x1 + 3 * mt * t * t * c.x2 + t * t * t * c.x,
            mt * mt * mt * cur.y + 3 * mt * mt * t * c.y1 + 3 * mt * t * t * c.y2 + t * t * t * c.y
          );
        }
        cur = { x: c.x, y: c.y };
        break;
      case 'Z':
        out.push('Z');
        cur = start;
        break;
    }
  }
  if (!out.length || !Number.isFinite(gMinX)) return null;

  return {
    paths: [`<path d="${out.join('')}"/>`],
    renderFontSize: fontSize,
    renderStrokeWidth,
    centerOffset: {
      x: spec.dstWidth / 2 - (gMinX + gMaxX) / 2,
      y: spec.dstHeight / 2 - (gMinY + gMaxY) / 2,
    },
  };
}

/**
 * Bounding box over every coordinate in the commands, control points
 * included — the builder measures its raster the same way (a numeric scan of
 * the path's SVG), so the two canvases agree.
 */
function commandsBBox(
  commands: PathCommand[]
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const take = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const c of commands) {
    if (c.type === 'Z') continue;
    take(c.x, c.y);
    if (c.type === 'Q' || c.type === 'C') take(c.x1, c.y1);
    if (c.type === 'C') take(c.x2, c.y2);
  }
  return Number.isFinite(minX) && Number.isFinite(minY) ? { minX, minY, maxX, maxY } : null;
}

function pathData(path: ArcPathLike): string {
  if (typeof path.toPathData === 'function') return path.toPathData(2);
  if (typeof path.toSVG === 'function') return path.toSVG(2).match(/d="([^"]*)"/)?.[1] ?? '';
  return '';
}
